import {
  Form,
  Modal,
  Spin,
  message,
  Upload,
  Switch,
  Col,
  Row
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { useRef, useState, useEffect } from "react";
import api from "../../hooks/api";
import axiosInstance from "../../axios/axios-instance";
import { pathAPI } from "../../utils/pathApi";
import { fileToBlob } from "../../utils/utils";
import "./index.scss";
import { Box } from "@mui/material";
import ActionButton from "../common/buttons/ActionButton";
import CancelButton from "../common/buttons/CancelButton";
import InputField from "../common/fields/InputField";
import SelectField from "../common/fields/SelectField";
import { FormInstance } from "antd/lib";
import { Typography } from "@mui/material";
import { currency_list } from "../../utils/constants";

interface LanguageOption {
  id: number;
  name: string;
  language_code: string;
}

interface CountryData {
  id?: number;
  name: string;
  code: string;
  flag?: string;
  currency: string;
  phone_code: string;
  language_id: number;
  active: boolean;
}

interface AddCountryProps {
  open: boolean;
  onCancel: () => void;
  reloadData: () => void;
  countryData?: CountryData;
}

const AddCountry: React.FC<AddCountryProps> = ({
  reloadData,
  onCancel,
  countryData,
  ...props
}) => {
  const formRef = useRef<FormInstance>(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [languages, setLanguages] = useState<LanguageOption[]>([]);
  const [flagImage, setFlagImage] = useState<File | null>(null);
  const [fileList, setFileList] = useState<any[]>([]);
  const { Dragger } = Upload;
  const [messageApi, contextHolder] = message.useMessage();
  // Bumps per open so a late response from a previous item is ignored.
  const requestSeqRef = useRef(0);

  // Fetch languages and country data when modal opens
  useEffect(() => {
    if (!props.open) return;
    const seq = ++requestSeqRef.current;
    form.resetFields();
    fetchLanguages(seq);

    if (countryData?.id) {
      setFormLoading(true);
      fetchCountryDetails(countryData.id, seq);
    } else {
      setFormLoading(false);
    }
  }, [props.open, countryData?.id]);

  // Fetch language options (simplified, no loading state)
  const fetchLanguages = async (seq: number) => {
    try {
      const response = await axiosInstance.get(pathAPI.languages);
      if (seq !== requestSeqRef.current) return;
      if (response.data?.data) {
        setLanguages(response.data.data);
      }
    } catch (error) {
      if (seq !== requestSeqRef.current) return;
      console.error("Error fetching languages:", error);
      messageApi.error("Failed to load languages");
    }
  };

  // Fetch country details
  const fetchCountryDetails = async (id: number, seq: number) => {
    try {
      const response = await axiosInstance.get(`${pathAPI.countries}/${id}`);
      if (seq !== requestSeqRef.current) return;
      if (response.data?.data) {
        const country = response.data.data;
        form.setFieldsValue({
          name: country.name,
          code: country.code,
          phone_code: country.phone_code,
          language_id: country.language_id,
          currency: country.currency,
          active: country.active
        });
      }
    } catch (error) {
      if (seq !== requestSeqRef.current) return;
      console.error("Error fetching country details:", error);
      messageApi.error("Failed to fetch country details");
    } finally {
      if (seq === requestSeqRef.current) setFormLoading(false);
    }
  };

  // Save country data
  const saveCountry = async (data: FormData) => {
    try {
      setLoading(true);
      
      const xhr = new XMLHttpRequest();
      const token = localStorage.getItem("token");
      const baseUrl = process.env.REACT_APP_SERVER_DEV_ENDPOINT || '';
      const apiVersion = process.env.REACT_APP_API_VERSION || '';
      const url = countryData?.id 
        ? `${baseUrl}/${apiVersion}/${pathAPI.countries}/${countryData.id}`
        : `${baseUrl}/${apiVersion}/${pathAPI.countries}`;
      
      xhr.open(countryData?.id ? 'PUT' : 'POST', url);
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      
      xhr.onload = function() {
        setLoading(false);
        
        if (xhr.status >= 200 && xhr.status < 300) {
          messageApi.success(`Country ${countryData?.id ? 'updated' : 'created'} successfully!`);
          reloadData();
          onCancel();
          form.resetFields();
          setFlagImage(null);
          setFileList([]);
        } else {
          console.error("Save error:", xhr.status, xhr.responseText);
          messageApi.error(`Failed to ${countryData?.id ? 'update' : 'create'} country. Server error.`);
        }
      };
      
      xhr.onerror = function() {
        setLoading(false);
        messageApi.error(`Failed to ${countryData?.id ? 'update' : 'create'} country. Network error.`);
      };
      
      xhr.send(data);
    } catch (error) {
      setLoading(false);
      console.error("Save error:", error);
      messageApi.error(`Failed to ${countryData?.id ? 'update' : 'create'} country`);
    }
  };

  // Form submission handler
  const onFinish = async () => {
    try {
      await form.validateFields();
      
      const formData = new FormData();
      
      // Create model object with form values
      const modelObj = {
        name: form.getFieldValue("name"),
        code: form.getFieldValue("code"),
        phone_code: form.getFieldValue("phone_code"),
        language_id: form.getFieldValue("language_id"),
        currency: form.getFieldValue("currency"),
        active: form.getFieldValue("active") === true
      };
      
      // Add flag file if selected
      if (flagImage && flagImage instanceof File) {
        formData.append('flagFile', flagImage, flagImage.name || 'flag.png');
      }
      
      // Add data as application/json blob
      formData.append('data', new Blob([JSON.stringify(modelObj)], {
        type: 'application/json'
      }));
      
      // Save country data
      saveCountry(formData);
    } catch (error) {
      console.error("Error in form submission:", error);
    }
  };

  // Close modal handler
  const handleCloseModal = () => {
    form.resetFields();
    setFlagImage(null);
    setFileList([]);
    onCancel();
  };

  return (
    <Modal
      title={countryData?.id ? "Edit Country" : "Add Country"}
      open={props.open}
      onCancel={handleCloseModal}
      width={600}
      footer={
        <Box display="flex" justifyContent="flex-end" gap={1.5}>
          <CancelButton onClick={handleCloseModal}>Cancel</CancelButton>
          <ActionButton onClick={onFinish} loading={loading} disabled={formLoading}>
            {countryData?.id ? "Update" : "Create"}
          </ActionButton>
        </Box>
      }
    >
      {contextHolder}
      {formLoading ? (
        <div style={{ textAlign: 'center', padding: '30px' }}>
          <Spin size="large" tip="Loading country data..." />
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          initialValues={{ active: true }}
        >
          <Row gutter={[16, 16]}>
            {/* Basic fields */}
            <Col span={12}>
              <Form.Item
                name="name"
                label="Country Name"
                rules={[{ required: true, message: "Please enter country name" }]}
              >
                <InputField placeholder="Enter country name" />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="code"
                label="Country Code"
                rules={[{ required: true, message: "Please enter country code" }]}
              >
                <InputField placeholder="Enter country code" />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="currency"
                label="Currency"
                rules={[{ required: true, message: "Please select currency" }]}
              >
                <SelectField
                  placeholder="Select currency"
                  options={currency_list.map((item) => ({
                    value: item.value,
                    label: `${item.name} (${item.value})`,
                  }))}
                />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="phone_code"
                label="Phone Code"
                rules={[{ required: true, message: "Please enter phone code" }]}
              >
                <InputField placeholder="Enter phone code (e.g. +1)" />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="language_id"
                label="Language"
                rules={[{ required: true, message: "Please select language" }]}
              >
                <SelectField
                  placeholder="Select language"
                  searchable={false}
                  matchAnchorWidth
                  options={languages.map((lang) => ({
                    value: lang.id,
                    label: `${lang.name} (${lang.language_code})`,
                  }))}
                />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="active"
                label="Status"
                valuePropName="checked"
                initialValue={true}
              >
                <Switch 
                  checkedChildren="Active" 
                  unCheckedChildren="Inactive" 
                />
              </Form.Item>
            </Col>
            
            {/* Flag Upload */}
            <Col span={24}>
              <Form.Item
                name="flag"
                label="Flag"
                rules={[
                  {
                    required: !countryData?.id,
                    message: "Please upload a flag image",
                  },
                ]}
              >
                <div>
                  {/* Display current flag if editing */}
                  {countryData?.flag && (
                    <div style={{ marginBottom: '15px' }}>
                      <img 
                        src={countryData.flag} 
                        alt="Current flag" 
                        style={{ 
                          maxWidth: '150px',
                          height: '100px',
                          objectFit: 'contain' as const,
                          border: '1px solid var(--app-border-light)',
                          borderRadius: '4px',
                          padding: '4px',
                          marginBottom: '10px'
                        }} 
                      />
                    </div>
                  )}
                  
                  {/* Upload control */}
                  <Dragger
                    fileList={fileList}
                    multiple={false}
                    beforeUpload={(file) => {
                      return false; // Prevent auto-upload
                    }}
                    onChange={(info) => {
                      // Update the fileList for the UI
                      setFileList(info.fileList.slice(-1));
                      
                      // Store the actual File object for submission
                      if (info.file.originFileObj) {
                        setFlagImage(info.file.originFileObj);
                      } else if (info.file instanceof File) {
                        setFlagImage(info.file);
                      }
                    }}
                    onRemove={() => {
                      setFileList([]);
                      setFlagImage(null);
                      return true;
                    }}
                    accept="image/*"
                    listType="picture"
                  >
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">
                      Click or drag image here to upload
                    </p>
                    <p className="ant-upload-hint">
                      {countryData?.id ? 'Upload new flag (optional)' : 'Upload flag image (required)'}
                    </p>
                  </Dragger>
                </div>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      )}
    </Modal>
  );
};

export default AddCountry;
